import { useEffect, useState } from "react";
import {
  applyTheme,
  getLocale,
  getTheme,
  setLocale,
  t,
  type ThemeId
} from "../i18n";
import type { PanelLocale } from "../types";
import { api } from "../api";

export function PolishPage({ isStaff }: { isStaff: boolean }) {
  const [theme, setThemeState] = useState(getTheme());
  const [locale, setLocaleState] = useState(getLocale());
  const [plugins, setPlugins] = useState<
    {
      id: string;
      name: string;
      version: string;
      description: string;
      enabled: boolean;
      hooks: string[];
    }[]
  >([]);
  const [cache, setCache] = useState<Record<string, unknown> | null>(null);
  const [gitUserId, setGitUserId] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [gitPath, setGitPath] = useState("app");
  const [gitMsg, setGitMsg] = useState("");
  const [error, setError] = useState("");
  const [accounts, setAccounts] = useState<{ id: string; username: string }[]>(
    []
  );

  const loadExtras = async () => {
    try {
      if (isStaff) {
        const p = await api<{ ok: boolean; items: typeof plugins }>(
          "/api/v1/plugins"
        );
        setPlugins(p.items || []);
      }
      const c = await api<{ ok: boolean; redis: unknown; memcached: unknown }>(
        "/api/v1/cache/status"
      );
      setCache({ redis: c.redis, memcached: c.memcached });
      const a = await api<{
        ok: boolean;
        items: { id: string; username: string; role: string }[];
      }>("/api/v1/accounts");
      setAccounts(
        (a.items || [])
          .filter((x) => x.role === "user")
          .map((x) => ({ id: x.id, username: x.username }))
      );
      if (!gitUserId && a.items?.[0]) {
        const u = a.items.find((x) => x.role === "user");
        if (u) setGitUserId(u.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void loadExtras();
  }, []);

  const onTheme = (th: ThemeId) => {
    setThemeState(th);
    applyTheme(th);
  };

  const onLocale = (loc: PanelLocale) => {
    setLocaleState(loc);
    setLocale(loc);
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>{t("polish.title") || "Appearance & Plugins"}</h1>
          <p>
            Theme, language packs, plugin hooks, git deploy, and cache helpers.
            See <code className="mono">docs/feature-matrix.md</code> for deferred
            marketplace items.
          </p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void loadExtras()}
          >
            {t("common.refresh")}
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-label">{t("polish.theme")}</div>
        <div className="chip-row">
          {(["light", "dark", "midnight", "system"] as ThemeId[]).map((th) => (
            <button
              key={th}
              type="button"
              className={`chip ${theme === th ? "active" : ""}`}
              onClick={() => onTheme(th)}
            >
              {t(`theme.${th}`)}
              {th === "light" ? " · default" : ""}
            </button>
          ))}
        </div>
        <p className="card-meta" style={{ marginTop: 12 }}>
          Premium light is the default SaaS experience — clean surfaces, soft
          shadows, and indigo accents across the panel.
        </p>
      </section>

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-label">{t("polish.language")}</div>
        <div className="chip-row">
          {(
            [
              ["en", "English"],
              ["es", "Español"],
              ["ar", "العربية"]
            ] as const
          ).map(([code, label]) => (
            <button
              key={code}
              type="button"
              className={`chip ${locale === code ? "active" : ""}`}
              onClick={() => onLocale(code)}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-label">{t("git.title")}</div>
        <p className="card-meta">
          Clone or pull a repository into an account home (jailed). No free-form
          shell post-deploy.
        </p>
        <div className="form-grid">
          <div className="field">
            <label>Account</label>
            <select
              value={gitUserId}
              onChange={(e) => setGitUserId(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Repo URL</label>
            <input
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="https://github.com/org/repo.git"
            />
          </div>
          <div className="field">
            <label>Path under home</label>
            <input
              value={gitPath}
              onChange={(e) => setGitPath(e.target.value)}
              placeholder="app"
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-inline"
          onClick={async () => {
            setGitMsg("");
            setError("");
            try {
              const res = await api<{
                ok: boolean;
                result: { action?: string; path?: string };
              }>("/api/v1/git/deploy", {
                method: "POST",
                body: JSON.stringify({
                  userId: gitUserId,
                  repoUrl: repoUrl || undefined,
                  path: gitPath
                })
              });
              setGitMsg(
                `${res.result?.action || "ok"} → ${res.result?.path || ""}`
              );
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            }
          }}
        >
          Deploy
        </button>
        {gitMsg ? <p className="card-meta mono">{gitMsg}</p> : null}
      </section>

      {cache ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-label">Redis / Memcached helpers</div>
          <pre className="mono" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(cache, null, 2)}
          </pre>
        </section>
      ) : null}

      {isStaff ? (
        <section className="card">
          <div className="card-label">{t("plugins.title")} (SDK v1)</div>
          <p className="card-meta">
            Register hooks: account.after_create, site.after_create, etc. Optional{" "}
            <code className="mono">config.webhookUrl</code> for HTTP callbacks.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Hooks</th>
                  <th>Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {plugins.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">{p.id}</td>
                    <td>
                      {p.name}{" "}
                      <span className="faint mono">{p.version}</span>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {p.hooks.join(", ")}
                    </td>
                    <td>{p.enabled ? "yes" : "no"}</td>
                    <td>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={async () => {
                          await api(`/api/v1/plugins/${p.id}`, {
                            method: "PATCH",
                            body: JSON.stringify({ enabled: !p.enabled })
                          });
                          await loadExtras();
                        }}
                      >
                        {p.enabled ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}
